import LoadingOverlay from '../../components/feedback/LoadingOverlay'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { requireNotUndefinedOrNull } from '../../util/require'
import { EntityTypesContext } from '../../io/context/EntityTypes'
import { getTypeDisplayName } from '../../util/entityType/typeNameSchema'
import { isManualOrderField } from '../../util/entityType/orderField'
import { com, kotlin } from '@eidu/form'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import { Button, Stack, Typography } from '@mui/material'
import EntityId = com.eidu.sharedlib.entity.EntityId
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import entityIdFromString = com.eidu.sharedlib.entity.entityIdFromString
import entityTypeIdFromString = com.eidu.sharedlib.entity.type.entityTypeIdFromString
import useEntityRepository from '../../io/useEntityRepository'
import { createAuthContext } from '../../api/authorization/AuthenticationContext'
import { AuthContext, IAuthContext } from 'react-oauth2-code-pkce'
import usePermittedActions from '../../io/usePermittedActions'
import { useSnackBar } from '../../components/feedback/SnackBarProvider'
import DraftFieldValue, {
  draftFieldValuesToFieldValues,
  fieldValueToDraftFieldValue,
  fieldValueToString,
} from '../../domain/entity/DraftFieldValue'
import EqualityHashMap from '../../util/EqualityHashMap'
import { isNotUndefinedOrNull } from '../../util/predicates'
import getFormContent from '../../api/form/getFormContent'
import hasFieldError from '../../domain/entity/hasFieldError'
import postFormContent from '../../api/form/postFormContent'
import Link from '../../domain/entity/Link'
import createEntityToCreate from './createEntityToCreate'
import { logException } from '../../util/Logging'
import EntityFormInput from '../../components/input/EntityFormInput'
import FormContentInput from '../../components/input/FormContentInput'
import ConfirmationDialog from '../../components/feedback/ConfirmationDialog'
import PermissionError from './PermissionError'
import { ReadyCreateOrEditPageProps } from './CreateOrEditPage'
import EntityAction = com.eidu.sharedlib.entity.permission.EntityAction
import PermittedGlobalAction = com.eidu.sharedlib.entity.permission.PermittedGlobalAction
import PermittedEntityAction = com.eidu.sharedlib.entity.permission.PermittedEntityAction
import GlobalAction = com.eidu.sharedlib.entity.permission.GlobalAction
import FieldId = com.eidu.sharedlib.entity.field.FieldId
import getFormContentId = com.eidu.sharedlib.entity.getFormContentId
import EntityTypeKind = com.eidu.sharedlib.entity.type.EntityTypeKind
import KtList = kotlin.collections.KtList
import Page = com.eidu.sharedlib.form.definition.Page
import FormContentIdAndHash from '../../domain/form/FormContentIdAndHash'
import formContentHashKey = com.eidu.sharedlib.entity.formContentHashKey
import FieldValue = com.eidu.sharedlib.entity.field.FieldValue
import Entity = com.eidu.sharedlib.entity.Entity
import formContentIdKey = com.eidu.sharedlib.entity.formContentIdKey
import postForm from '../../api/form/postForm'
import FormContent = com.eidu.sharedlib.form.FormContent
import NonEmptyList = com.quickbirdstudios.nonEmptyCollection.list.NonEmptyList
import { useFormErrors } from '../../util/FormErrorBus'
import FormValidator = com.eidu.sharedlib.form.validation.FormValidator
import toException = com.eidu.sharedlib.form.validation.toException

type ReadyCreateOrEditFormProps = {
  entityId?: EntityId
  entityType: EntityType
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>
}

type CreatePrefillState = {
  hierarchyCreatePrefill?: Array<{
    fieldId: string
    value: string
  }>
}

const getNonNullFieldsFromEntity = (entity: Entity): ReadonlyMap<FieldId, DraftFieldValue> =>
  new EqualityHashMap<FieldId, DraftFieldValue>(
    Array.from(entity.valuesByFieldId.asJsReadonlyMapView())
      .filter((it): it is [FieldId, FieldValue] => it[1] !== null)
      .map(([id, value]) => [id, fieldValueToDraftFieldValue(id, value)])
  )

const ReadyCreateOrEditForm: React.FC<ReadyCreateOrEditFormProps> = ({
  entityId,
  entityType,
  entityTypes,
}: ReadyCreateOrEditPageProps) => {
  const entityRepository = useEntityRepository(
    {
      typeId: entityType.id,
      types: entityTypes,
    },
    [entityType.id.toString(), entityTypes.size]
  )

  const authContext = createAuthContext(useContext<IAuthContext>(AuthContext))

  const requiredEntityActions: EntityAction[] = [EntityAction.ReadEntity, EntityAction.UpdateEntity]

  const requested = useRef([
    new PermittedGlobalAction(GlobalAction.ManageAuthentication),
    ...(entityId ? requiredEntityActions.map((entityAction) => new PermittedEntityAction(entityAction, entityId)) : []),
  ])
  const [permissions] = usePermittedActions(requested.current)

  const missingEntityActions = entityId
    ? requiredEntityActions.filter(
        (action) =>
          !permissions?.some(
            (permittedAction) =>
              permittedAction instanceof PermittedEntityAction &&
              permittedAction.target.equals(entityId) &&
              permittedAction.action === action
          )
      )
    : []

  const hasRequiredEntityActions = missingEntityActions.length === 0

  // Important: entityId must not change between undefined and
  // not undefined, otherwise React's hook order will be messed up.
  const entity = entityId !== undefined ? entityRepository.useItem(entityId) : undefined

  const navigate = useNavigate()
  const location = useLocation()
  const { showSnackBar } = useSnackBar()

  const { hasErrors: hasFormErrors } = useFormErrors()
  const [formContentLoading, setFormContentLoading] = useState(false)

  // Important: all use* need to run, so don't replace this by anything using short-circuiting!
  const error = [entityRepository.useError(), entity === null ? 'Entity not found' : undefined]
    .find((it) => !!it)
    ?.let((it) => String(it))
  const loading =
    [entityRepository.useLoading(), entityId !== undefined && entity === undefined].some((it) => it) ||
    formContentLoading

  const [submitting, setSubmitting] = useState(false)

  const [fields, setFields] = useState<ReadonlyMap<FieldId, DraftFieldValue>>(new Map())
  const updateField = (value: DraftFieldValue) => setFields((it) => new EqualityHashMap(it).set(value.fieldId, value))

  const [formContent, setFormContent] = useState<KtList<Page>>()
  const [formContentErrorMessage, setFormContentErrorMessage] = useState<string>()

  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)

  const prefillByFieldId = useMemo(() => {
    if (entityId) return new Map<string, string>()
    const state = location.state as CreatePrefillState | null
    const prefill = Array.isArray(state?.hierarchyCreatePrefill) ? state.hierarchyCreatePrefill : []
    return new Map(
      prefill
        .filter(
          (entry): entry is { fieldId: string; value: string } =>
            typeof entry?.fieldId === 'string' && typeof entry.value === 'string'
        )
        .map((entry) => [entry.fieldId, entry.value])
    )
  }, [entityId, location.state])

  useEffect(() => {
    if (entity) {
      setFields(getNonNullFieldsFromEntity(entity.entity))

      const formContentId = getFormContentId(entity.entity) ?? undefined
      if (isNotUndefinedOrNull(formContentId)) {
        setFormContentLoading(true)
        getFormContent({
          formId: entity.entity.typeId.toString(),
          formContentId: formContentId.toString(),
          authContext: requireNotUndefinedOrNull(authContext),
        }).then((content) => {
          setFormContentLoading(false)
          const formContentWithPageKeys = KtList.fromJsArray(
            content
              .asJsReadonlyArrayView()
              .map((page: Page, index: number) =>
                !page.key ? page.copy(page.title, page.items, `page_${Date.now()}_${index}`) : page
              )
          )
          setFormContent(formContentWithPageKeys)
          setFormContentLoading(false)
        })
      } else {
        setFormContent(undefined)
      }
    } else {
      const initialValues = new EqualityHashMap<FieldId, DraftFieldValue>()
      entityType.fields.asJsReadonlyArrayView().forEach((field) => {
        const prefillValue = prefillByFieldId.get(field.id.toString())
        if (prefillValue === undefined) return
        initialValues.set(field.id, {
          fieldId: field.id,
          type: field.type,
          value: prefillValue,
        })
      })
      setFields(initialValues)
    }
  }, [entity, entityType, prefillByFieldId])

  const valid = useMemo(() => {
    if (!entityType) return false

    const fieldsValid = entityType.fields.asJsReadonlyArrayView().every((field) => {
      const fieldValue = fields.get(field.id) ?? undefined
      return !hasFieldError(field, fieldValue, new Map())
    })

    return fieldsValid && !hasFormErrors
  }, [entityType, fields, hasAttemptedSubmit])

  const getFieldValues = (): DraftFieldValue[] =>
    entity
      ? [...Array.from(fields.values())].filter((field) => {
          const originalValue = entity.entity.valuesByFieldId.asJsReadonlyMapView().get(field.fieldId)
          return !originalValue || fieldValueToString(originalValue) !== field.value
        })
      : [...Array.from(fields.values())]

  const createEntity = async () => {
    return await entityRepository.createEntity(createEntityToCreate(entityType.id, getFieldValues(), [], new Map()))
  }

  const createForm = async () => {
    try {
      const pages = formContent
      if (!pages) throw Error('Unable to create form: form content is undefined')
      const validationErrors = FormValidator.validatePages(KtList.fromJsArray(pages.asJsReadonlyArrayView()))
      if (validationErrors.asJsReadonlyArrayView().length > 0) throw toException(validationErrors)
      const entity = await createEntity()
      const contentIdAndHash = await postForm({
        formId: entity.toString(),
        initialContent: new FormContent(
          crypto.randomUUID(),
          NonEmptyList.fromArray([...pages.asJsReadonlyArrayView()])
        ),
        authContext: requireNotUndefinedOrNull(authContext),
      })
      const updatedLinks = createFormLinks(contentIdAndHash)
      await entityRepository.modifyEntity(entity, {
        valuesToUpdateByFieldId: draftFieldValuesToFieldValues(getFieldValues()),
        linksToUpdate: new Map(updatedLinks.map((link) => [link.key, link.value])),
        externalValuesToUpdate: new Map(),
      })
      showSnackBar(`Form created`, 'success')
      navigate(-1)
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      setFormContentErrorMessage(message)
      logException(err)
    }
  }

  const updateFormContent = async (entityId: EntityId) => {
    try {
      const pages = formContent
      if (!pages) throw Error('Unable to create form: form content is undefined')
      const validationErrors = FormValidator.validatePages(KtList.fromJsArray(pages.asJsReadonlyArrayView()))
      if (validationErrors.asJsReadonlyArrayView().length > 0) throw toException(validationErrors)
      const contentIdAndHash = await postFormContent({
        formId: entityId.toString(),
        content: new FormContent(crypto.randomUUID(), NonEmptyList.fromArray([...pages.asJsReadonlyArrayView()])),
        authContext: requireNotUndefinedOrNull(authContext),
      })
      const updatedLinks = createFormLinks(contentIdAndHash)
      await entityRepository.modifyEntity(entityId, {
        valuesToUpdateByFieldId: draftFieldValuesToFieldValues(getFieldValues()),
        linksToUpdate: new Map(updatedLinks.map((link) => [link.key, link.value])),
        externalValuesToUpdate: new Map(),
      })
      showSnackBar(`Form updated`, 'success')
      navigate(-1)
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      setFormContentErrorMessage(message)
      logException(err)
    }
  }

  const createFormLinks = (contentIdAndHash: FormContentIdAndHash): Link[] => {
    return [
      { key: formContentIdKey, value: contentIdAndHash.contentId.toString() },
      { key: formContentHashKey, value: contentIdAndHash.contentSha256 },
    ]
  }

  const submit = useCallback(async () => {
    setHasAttemptedSubmit(true)

    setSubmitting(true)
    try {
      if (!entity) {
        console.log('Creating Form')
        await createForm()
      } else {
        console.log('Updating form content')
        await updateFormContent(entity.entity.id)
      }
    } finally {
      setSubmitting(false)
    }
  }, [entityRepository, navigate, entityType, entity, fields, formContent, showSnackBar])

  const confirmDelete = async () => {
    const localEntityType = requireNotUndefinedOrNull(entityType)
    try {
      await entityRepository.deleteEntity(requireNotUndefinedOrNull(entityId))
      showSnackBar(`Deleted ${getTypeDisplayName(localEntityType.name)}`, 'success')
      navigate(`/entities/${localEntityType.id.toString()}`)
    } catch (e) {
      logException(e)
      showSnackBar(`Failed to delete ${getTypeDisplayName(localEntityType.name)}`, 'error')
    }
  }

  const loadingOverlayOpen = submitting || loading || !(error || entityType)
  return (
    <Stack padding={3} spacing={3}>
      {entityType && hasRequiredEntityActions && (
        <>
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h4">{getTypeDisplayName(entityType.name)}</Typography>
          </Stack>
          <Stack padding={0} spacing={3}>
            {entityType.fields
              .asJsReadonlyArrayView()
              .filter((field) => !isManualOrderField(field))
              .map((field) => (
                <EntityFormInput
                  key={`entity-form-field-${field.id.toString()}`}
                  field={field}
                  fieldValue={fields.get(field.id)}
                  entityTypes={entityTypes}
                  updateField={updateField}
                />
              ))}
          </Stack>
          {entityType.kind === EntityTypeKind.Form && (
            <FormContentInput
              pages={
                formContent ||
                KtList.fromJsArray([new Page('Untitled page', KtList.fromJsArray([]), `page_${Date.now()}`, null)])
              }
              onChange={setFormContent}
              errorMessage={formContentErrorMessage}
            />
          )}
          <Stack direction="row" spacing={3} sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate(-1)}>Cancel</Button>
            {entity && (
              <ConfirmationDialog
                title={`There may still be references to this ${
                  entityType ? getTypeDisplayName(entityType.name) : 'entity'
                } in the system. Are you sure you want to
                      delete this ${entityType ? getTypeDisplayName(entityType.name) : 'entity'}? This action cannot be undone.`}
                content={<Typography>Delete</Typography>}
                onConfirm={confirmDelete}
              />
            )}
            <Button variant="contained" onClick={submit} disabled={!valid}>
              Save
            </Button>
          </Stack>
        </>
      )}
      {!hasRequiredEntityActions && !loadingOverlayOpen && (
        <PermissionError missingPermittedActions={missingEntityActions} />
      )}
      <LoadingOverlay isOpen={loadingOverlayOpen} />
    </Stack>
  )
}

const CreateOrEditFormPage = () => {
  const params = useParams()
  const entityId = params.entityId?.let(entityIdFromString)
  const entityTypeId = entityTypeIdFromString(requireNotUndefinedOrNull(params.entityTypeId))

  const entityTypeRepository = useContext(EntityTypesContext)
  const entityTypes = entityTypeRepository.useAll()
  const entityType = entityTypeRepository.useItem(entityTypeId)
  const error = entityTypeRepository.useError()?.let((it) => String(it))

  return (
    <>
      {entityType && entityTypes && (
        <ReadyCreateOrEditForm entityId={entityId} entityType={entityType} entityTypes={entityTypes} />
      )}
      <LoadingOverlay isOpen={!(error || entityType)} />
    </>
  )
}

export default CreateOrEditFormPage
